/**
 * `/audit` — the server's records of what was done, for staff, in four tabs:
 *
 *   * WEB GRANTS (`web.audit_log`): every grant the mod applied on the web's behalf, newest first;
 *   * STAFF ACTIONS (`web.audit_event`): the audit log `/audit` reads in game — every command a person ran and
 *     every staff action a module recorded;
 *   * COBBLEMON LEDGER (`web.cobblemon_ledger`): which Cobblemon changed hands, and how;
 *   * REPORTS (`web.player_report`): the report queue, read-only.
 *
 * ALL FOUR ARE STAFF-GLOBAL AND READ-ONLY. They are views on the `web_read` plane, which every staff tier reads
 * alike (owner ruling 2026-11-01: "a table that is not player owned which there is a global read permission for
 * staff"; docs/design/README.md decision 1). The player plane cannot name the schema they live in, and nothing
 * here can act on a record: restoring a Cobblemon, claiming or closing a report stay in game, where they are
 * recorded. Who may do those is LuckPerms' answer, not this page's.
 *
 * THE WEB-GRANT TAB IS THE ONE SURFACE NOBODY READING IT CAN SHAPE. The web holds no privilege at all on
 * `web_audit`: not UPDATE, not DELETE, not even INSERT. Rows there are written by the mod after it has applied an
 * intent, so the tab is evidence rather than a log the thing being logged also owns. It is not the same set as
 * "grants in flight": a REFUSED intent settles `failed` in `web.outbox_status` and leaves nothing here, and an
 * audit row outlives the intent that caused it (migration 1.8 says why).
 *
 * THE OTHER THREE ARE PAGED IN THE DATABASE, NOT IN THE BROWSER. The command log alone grows by every command
 * anybody types, so those tabs ask PostgREST for one page (and one row more, which is how a page knows there is an
 * older one) and filter there. The web-grant tab is small and filters in the browser, as it always has.
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import {
  asIntentDocument,
  isStaff,
  STAFF_MIN_ROLE,
  type AuditEventRow,
  type AuditLogRow,
  type CobblemonLedgerRow,
  type PlayerReportRow,
} from '@dimensionscore/shared';

import type { ApiClient } from '../api/client';
import { usePolledView, type PolledView } from '../api/usePolledView';
import { useSession } from '../auth/SessionProvider';
import { SignInPanel } from '../components/SessionBar';

/**
 * How many rows the web-grant tab holds.
 *
 * A ceiling rather than a pager because the web-grant audit is append-only, small, and read newest-first: an admin
 * asking "what happened today" is served by the top of the list. The sidecar clamps the row count independently
 * (`dbMaxRows`), so this number is a request, not a promise.
 */
export const AUDIT_PAGE_ROWS = 100;

/** How many rows one page of a paged tab holds. One more is asked for, to know whether an older page exists. */
export const AUDIT_TAB_PAGE_ROWS = 50;

/** The four tabs, in order. The id is the `?tab=` value; the web-grant tab is the default and has none. */
export const AUDIT_TABS = [
  { id: 'grants', label: 'Web grants' },
  { id: 'staff', label: 'Staff actions' },
  { id: 'ledger', label: 'Cobblemon ledger' },
  { id: 'reports', label: 'Reports' },
] as const;

export type AuditTab = (typeof AUDIT_TABS)[number]['id'];

function asTab(value: string | null): AuditTab {
  return AUDIT_TABS.some((tab) => tab.id === value) ? (value as AuditTab) : 'grants';
}

export function AuditPage(): React.JSX.Element {
  const { session, client } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = asTab(params.get('tab'));

  /*
   * STAFF ONLY, AND SAID HERE BECAUSE THE TIER LADDER GREW A MEMBER BELOW `viewer`.
   *
   * The audit records are server-wide records of what staff did and what happened to players, and they are the
   * exact surface a self-service tier must not reach. The refusal is a courtesy, not the control:
   * `POSTGRES_READ_ROLE_FOR` maps `player` onto `web_player`, which holds no usage on schema `web` at all, and the
   * sidecar answers a player asking for any of these views with a 403. What this saves is a player staring at a
   * polling error every ten seconds with no idea why — and no tab below polls anything until this check passed.
   */
  const staff = session !== null && isStaff(session.claims.role);

  if (session === null) return <SignInPanel />;

  if (!staff) {
    return (
      <section className="dc-card" data-testid="page-audit-forbidden">
        <h1>Audit log</h1>
        <p className="dc-error" role="status" data-testid="audit-forbidden">
          The audit log is a record of what staff did across every player, so it needs the <code>{STAFF_MIN_ROLE}</code> tier or higher. This
          sign-in carries <code>{session.claims.role}</code>, which reads{' '}
          {/*
            Tier-accurate, because there are two non-staff tiers now and they are not the same claim:
            a `player` reads their own rows and an `unlinked` session reads nothing at all (its tier
            maps to no Postgres role). Saying "your own rows" to the second would be a promise the
            database does not keep.
          */}
          {session.claims.role === 'player' ? 'your own rows and nothing else' : 'nothing at all until a Minecraft account is linked to it'} — the
          database enforces that, not this page.
        </p>
      </section>
    );
  }

  return (
    <section className="dc-card" data-testid="page-audit">
      <h1>Audit log</h1>
      <div className="dc-tabs" role="tablist" aria-label="Audit records">
        {AUDIT_TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            className="dc-tab"
            aria-selected={entry.id === tab}
            data-testid={`audit-tab-${entry.id}`}
            onClick={() => setParams(entry.id === 'grants' ? {} : { tab: entry.id })}
          >
            {entry.label}
          </button>
        ))}
      </div>
      {/* One tab mounted at a time, so only the tab on screen polls. */}
      {tab === 'grants' && <WebGrantsTab client={client} />}
      {tab === 'staff' && <StaffActionsTab client={client} />}
      {tab === 'ledger' && <LedgerTab client={client} />}
      {tab === 'reports' && <ReportsTab client={client} />}
    </section>
  );
}

// ------------------------------------------------------------------------------------------ web grants

function WebGrantsTab({ client }: { client: ApiClient }): React.JSX.Element {
  const [term, setTerm] = useState('');
  const query = useMemo(() => ({ order: 'at.desc', limit: String(AUDIT_PAGE_ROWS) }), []);
  const audit = usePolledView(client, 'audit_log', query);

  const rows = (audit.rows ?? []) as readonly AuditLogRow[];
  /*
   * Filtered in the browser, not in PostgREST. The actor string carries a space and
   * parentheses — `discord:123 (Name)` — which are PostgREST filter syntax; an `ilike` over
   * it would need quoting rules this SPA would then own. The set is at most AUDIT_PAGE_ROWS
   * rows that have already arrived, so the filter is free and cannot be wrong.
   */
  const needle = term.trim().toLowerCase();
  const shown = needle === '' ? rows : rows.filter((row) => matches(row, needle));

  return (
    <div role="tabpanel" data-testid="audit-panel-grants">
      <p className="dc-dim">
        What the mod applied on the web’s behalf, written by the mod after the fact. A refused intent leaves no row here — those are in the outbox,
        on the player’s own page, with the reason the server gave.
      </p>

      <label className="dc-search">
        <span className="dc-dim">Filter by admin, player or action</span>
        <input
          type="search"
          data-testid="audit-search"
          value={term}
          placeholder="MockAdmin, reward.grant, a UUID…"
          autoComplete="off"
          onChange={(event) => setTerm(event.target.value)}
        />
      </label>

      {audit.status === 'error' ? (
        <p className="dc-error" role="status" data-testid="audit-error">
          {audit.error}
        </p>
      ) : shown.length === 0 && audit.status === 'ready' ? (
        <p className="dc-dim" data-testid="audit-empty">
          {rows.length === 0 ? 'Nothing has been granted through the web yet.' : `No audited grant matches ${term.trim()}.`}
        </p>
      ) : (
        <div className="dc-table-scroll">
          <table className="dc-table" data-testid="audit-table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Who asked</th>
                <th scope="col">Action</th>
                <th scope="col">Player</th>
                <th scope="col">What</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.id} data-testid="audit-row" data-audit-id={row.id}>
                  <td>
                    <When at={row.at} />
                  </td>
                  <td data-column="actor">{row.actor}</td>
                  <td data-column="action">
                    <code>{row.action}</code>
                  </td>
                  <td data-column="player">
                    <Link to={`/player/${row.player_uuid}`}>{row.last_known_name ?? <em className="dc-null">never joined</em>}</Link>
                  </td>
                  <td data-column="payload">
                    <Payload payload={row.payload} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * The intent document, shown as what it is when it is one and as JSON when it is not.
 *
 * `payload` is a `WebIntentPayload` — `{reward, description, displayItem}` — for the one
 * action that exists today. It is NOT assumed to be one: a future action will audit some
 * other document, and printing its JSON is honest where a blank cell would be a lie. There
 * is deliberately no per-reward-kind rendering; the reward is an open union and a `switch`
 * over it here would be the first place this tier grew an opinion about reward types.
 */
function Payload({ payload }: { payload: unknown }): React.JSX.Element {
  const intent = asIntentDocument(payload);
  if (intent === null) return <pre className="dc-json">{JSON.stringify(payload, null, 1)}</pre>;
  return (
    <>
      {intent.description !== null && <div data-audit-description>{intent.description}</div>}
      <pre className="dc-json">{JSON.stringify(intent.reward, null, 1)}</pre>
    </>
  );
}

/** Substring over the three human-readable columns, plus the payload as it is rendered. */
function matches(row: AuditLogRow, needle: string): boolean {
  const haystack = [row.actor, row.action, row.player_uuid, row.last_known_name ?? '', JSON.stringify(row.payload)].join(' ').toLowerCase();
  return haystack.includes(needle);
}

// ---------------------------------------------------------------------------------- the query language

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A player filter over several columns, as one PostgREST `or`: a UUID matches the uuid columns exactly, anything
 * else matches the name columns case-insensitively and whole (an `ilike` with no wildcard). A name is cut down to
 * the characters a Minecraft name can hold — or a console name — first, because `,` `.` `(` `)` and `*` are filter
 * syntax inside `or=(…)`; what is left of a term with none of those characters is no filter at all.
 */
export function playerFilter(term: string, uuidColumns: readonly string[], nameColumns: readonly string[]): string | null {
  const trimmed = term.trim();
  if (trimmed === '') return null;
  if (UUID_PATTERN.test(trimmed)) {
    const uuid = trimmed.toLowerCase();
    return `(${uuidColumns.map((column) => `${column}.eq.${uuid}`).join(',')})`;
  }
  const name = trimmed.replace(/[^A-Za-z0-9_]/g, '');
  if (name === '') return null;
  return `(${nameColumns.map((column) => `${column}.ilike.${name}`).join(',')})`;
}

/** An action or module prefix, `vaults` or `reward.give`: lower case, the characters an action is made of. */
function actionPrefix(term: string): string | null {
  const prefix = term.trim().toLowerCase().replace(/[^a-z0-9_.-]/g, '');
  return prefix === '' ? null : prefix;
}

/** One page of a paged tab, newest first, with one extra row asked for to learn whether an older page exists. */
function page(query: Record<string, string>, index: number): Record<string, string> {
  return { ...query, order: 'created_at.desc,id.desc', limit: String(AUDIT_TAB_PAGE_ROWS + 1), offset: String(index * AUDIT_TAB_PAGE_ROWS) };
}

/** The "since" choices: how far back, in milliseconds; 0 is everything. */
export const SINCE_CHOICES = [
  { id: 'all', label: 'Any time', millis: 0 },
  { id: '1h', label: 'The last hour', millis: 3_600_000 },
  { id: '1d', label: 'The last day', millis: 86_400_000 },
  { id: '7d', label: 'The last week', millis: 604_800_000 },
  { id: '30d', label: 'The last 30 days', millis: 2_592_000_000 },
] as const;

export interface StaffActionFilters {
  /** A name or a UUID; matches the record's actor or its target. */
  readonly player: string;
  /** An action or module prefix: `vaults`, `reward.give`, `command.tp`. */
  readonly action: string;
  /** The oldest instant to show, as ISO-8601, or `''` for everything. Fixed when chosen, so the query is stable. */
  readonly since: string;
}

/** The `web.audit_event` query for a set of filters and a page, from 0. Exported for the tests: it is the whole query language. */
export function staffActionsQuery(filters: StaffActionFilters, index: number): Record<string, string> {
  const query: Record<string, string> = {};
  const players = playerFilter(filters.player, ['player_uuid', 'actor_uuid'], ['last_known_name', 'actor_last_known_name', 'actor_name']);
  if (players !== null) query['or'] = players;
  const action = actionPrefix(filters.action);
  if (action !== null) query['action'] = `like.${action}*`;
  if (filters.since !== '') query['created_at'] = `gte.${filters.since}`;
  return page(query, index);
}

export interface LedgerFilters {
  /** A name or a UUID; matches the entry's owner or the player on the other side. */
  readonly player: string;
  /** An action, or a family by its first part: `trade` is `trade_out` and `trade_in`. */
  readonly action: string;
}

export function ledgerQuery(filters: LedgerFilters, index: number): Record<string, string> {
  const query: Record<string, string> = {};
  const players = playerFilter(filters.player, ['player_uuid', 'counterparty_uuid'], ['last_known_name', 'counterparty_last_known_name']);
  if (players !== null) query['or'] = players;
  const action = actionPrefix(filters.action);
  if (action !== null) query['action'] = `like.${action}*`;
  return page(query, index);
}

/** Which reports: the queue staff still owe an answer (open or claimed), the closed ones, or all. */
export type ReportStatusFilter = 'open' | 'closed' | 'all';

export interface ReportFilters {
  readonly status: ReportStatusFilter;
  /** A name or a UUID; matches whoever filed it or whoever it is about. */
  readonly player: string;
}

export function reportsQuery(filters: ReportFilters, index: number): Record<string, string> {
  const query: Record<string, string> = {};
  if (filters.status === 'open') query['status'] = 'in.(OPEN,CLAIMED)';
  if (filters.status === 'closed') query['status'] = 'eq.CLOSED';
  const players = playerFilter(filters.player, ['player_uuid', 'reported_uuid'], ['last_known_name', 'reported_last_known_name']);
  if (players !== null) query['or'] = players;
  return page(query, index);
}

// ------------------------------------------------------------------------------------------ staff actions

function StaffActionsTab({ client }: { client: ApiClient }): React.JSX.Element {
  const [player, setPlayer] = useState('');
  const [action, setAction] = useState('');
  const [since, setSince] = useState<{ choice: string; at: string }>({ choice: 'all', at: '' });
  const [index, setIndex] = useState(0);

  const query = useMemo(() => staffActionsQuery({ player, action, since: since.at }, index), [player, action, since, index]);
  const view = usePolledView(client, 'audit_event', query);
  const rows = (view.rows ?? []) as readonly AuditEventRow[];

  return (
    <div role="tabpanel" data-testid="audit-panel-staff">
      <p className="dc-dim">
        The audit log the server keeps, as <code>/audit</code> shows it in game: every command a person ran, and every staff action a module recorded —
        who did it, to whom, and where.
      </p>
      <div className="dc-filters">
        <FilterInput label="Player (by or to)" testId="staff-filter-player" value={player} placeholder="A name or a UUID" onChange={(value) => { setPlayer(value); setIndex(0); }} />
        <FilterInput label="Action or module" testId="staff-filter-action" value={action} placeholder="vaults, reward.give, command.tp" onChange={(value) => { setAction(value); setIndex(0); }} />
        <label className="dc-field">
          <span className="dc-dim">Since</span>
          <select
            data-testid="staff-filter-since"
            value={since.choice}
            onChange={(event) => {
              const choice = SINCE_CHOICES.find((entry) => entry.id === event.target.value) ?? SINCE_CHOICES[0];
              // Fixed now, not on every render: a moving bound would be a new query every render, and a poll forever.
              setSince({ choice: choice.id, at: choice.millis === 0 ? '' : new Date(Date.now() - choice.millis).toISOString() });
              setIndex(0);
            }}
          >
            {SINCE_CHOICES.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <PagedTable view={view} index={index} onPage={setIndex} testId="staff" empty="No record matches these filters.">
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Action</th>
            <th scope="col">By</th>
            <th scope="col">To</th>
            <th scope="col">What</th>
            <th scope="col">Where</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, AUDIT_TAB_PAGE_ROWS).map((row) => (
            <tr key={row.id} data-testid="staff-row" data-audit-id={row.id}>
              <td>
                <When at={row.created_at} />
              </td>
              <td data-column="action">
                <code>{row.action}</code>
              </td>
              <td data-column="actor">
                {row.actor_uuid === null ? (
                  <span title={row.actor_kind}>{row.actor_name}</span>
                ) : (
                  <PlayerLink uuid={row.actor_uuid} name={row.actor_name} now={row.actor_last_known_name} />
                )}
              </td>
              <td data-column="target">{row.player_uuid === null ? <span className="dc-null">—</span> : <PlayerLink uuid={row.player_uuid} name={row.last_known_name} />}</td>
              <td data-column="summary">{row.summary}</td>
              <td data-column="place">
                <Place dimension={row.dimension} x={row.x} y={row.y} z={row.z} />
              </td>
            </tr>
          ))}
        </tbody>
      </PagedTable>
    </div>
  );
}

// ------------------------------------------------------------------------------------------ the ledger

function LedgerTab({ client }: { client: ApiClient }): React.JSX.Element {
  const [player, setPlayer] = useState('');
  const [action, setAction] = useState('');
  const [index, setIndex] = useState(0);

  const query = useMemo(() => ledgerQuery({ player, action }, index), [player, action, index]);
  const view = usePolledView(client, 'cobblemon_ledger', query);
  const rows = (view.rows ?? []) as readonly CobblemonLedgerRow[];

  return (
    <div role="tabpanel" data-testid="audit-panel-ledger">
      <p className="dc-dim">
        Which Cobblemon changed hands, and how — trades, releases, gifts, clears, staff gives and restores. A restore is made in game, with{' '}
        <code>/audit cobblemon restore</code>.
      </p>
      <div className="dc-filters">
        <FilterInput label="Player (either side)" testId="ledger-filter-player" value={player} placeholder="A name or a UUID" onChange={(value) => { setPlayer(value); setIndex(0); }} />
        <FilterInput label="Action" testId="ledger-filter-action" value={action} placeholder="trade, release, gift_in" onChange={(value) => { setAction(value); setIndex(0); }} />
      </div>
      <PagedTable view={view} index={index} onPage={setIndex} testId="ledger" empty="No ledger entry matches these filters.">
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Player</th>
            <th scope="col">Action</th>
            <th scope="col">Cobblemon</th>
            <th scope="col">Other side</th>
            <th scope="col">Note</th>
            <th scope="col">Restored</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, AUDIT_TAB_PAGE_ROWS).map((row) => (
            <tr key={row.id} data-testid="ledger-row" data-ledger-id={row.id}>
              <td>
                <When at={row.created_at} />
              </td>
              <td data-column="owner">
                <PlayerLink uuid={row.player_uuid} name={row.last_known_name} />
              </td>
              <td data-column="action">
                <code>{row.action}</code>
              </td>
              <td data-column="cobblemon" title={`${row.cobblemon_uuid}${row.aspects === '' ? '' : ` · ${row.aspects}`}`}>
                {row.species} · level {row.level}
                {row.shiny && ' ✦'}
              </td>
              <td data-column="counterparty">
                {row.counterparty_uuid === null ? <span className="dc-null">nobody</span> : <PlayerLink uuid={row.counterparty_uuid} name={row.counterparty_last_known_name} />}
              </td>
              <td data-column="note">{row.note ?? ''}</td>
              <td data-column="restored">{row.restored_at === null ? <span className="dc-null">no</span> : <When at={row.restored_at} />}</td>
            </tr>
          ))}
        </tbody>
      </PagedTable>
    </div>
  );
}

// ------------------------------------------------------------------------------------------ reports

function ReportsTab({ client }: { client: ApiClient }): React.JSX.Element {
  const [status, setStatus] = useState<ReportStatusFilter>('open');
  const [player, setPlayer] = useState('');
  const [index, setIndex] = useState(0);

  const query = useMemo(() => reportsQuery({ status, player }, index), [status, player, index]);
  const view = usePolledView(client, 'player_report', query);
  const rows = (view.rows ?? []) as readonly PlayerReportRow[];

  return (
    <div role="tabpanel" data-testid="audit-panel-reports">
      <p className="dc-dim">
        What players reported, and what became of it. Claiming, closing and going there are done in game, with <code>/reports</code>.
      </p>
      <div className="dc-filters">
        <label className="dc-field">
          <span className="dc-dim">Show</span>
          <select
            data-testid="reports-filter-status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as ReportStatusFilter);
              setIndex(0);
            }}
          >
            <option value="open">Open and claimed</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
        </label>
        <FilterInput label="Player (who filed it, or about whom)" testId="reports-filter-player" value={player} placeholder="A name or a UUID" onChange={(value) => { setPlayer(value); setIndex(0); }} />
      </div>
      <PagedTable view={view} index={index} onPage={setIndex} testId="reports" empty="No report matches these filters.">
        <thead>
          <tr>
            <th scope="col">Filed</th>
            <th scope="col">By</th>
            <th scope="col">About</th>
            <th scope="col">Reason</th>
            <th scope="col">Status</th>
            <th scope="col">Handled by</th>
            <th scope="col">Where</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, AUDIT_TAB_PAGE_ROWS).map((row) => (
            <tr key={row.id} data-testid="reports-row" data-report-id={row.id}>
              <td>
                <When at={row.created_at} />
              </td>
              <td data-column="reporter">
                <PlayerLink uuid={row.player_uuid} name={row.last_known_name} />
              </td>
              <td data-column="reported">
                {row.reported_uuid === null ? <span className="dc-null">a help request</span> : <PlayerLink uuid={row.reported_uuid} name={row.reported_last_known_name} />}
              </td>
              <td data-column="reason">{row.reason}</td>
              <td data-column="status">
                {row.status}
                {row.closed_at !== null && (
                  <>
                    {' '}
                    <When at={row.closed_at} />
                  </>
                )}
              </td>
              <td data-column="handler">
                {row.handler_uuid === null ? <span className="dc-null">nobody yet</span> : <PlayerLink uuid={row.handler_uuid} name={row.handler_last_known_name} />}
                {row.note !== null && <div className="dc-dim">{row.note}</div>}
              </td>
              <td data-column="place">
                <Place dimension={row.dimension} x={row.x} y={row.y} z={row.z} />
              </td>
            </tr>
          ))}
        </tbody>
      </PagedTable>
    </div>
  );
}

// ------------------------------------------------------------------------------------------ shared parts

function FilterInput(props: {
  label: string;
  testId: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <label className="dc-field">
      <span className="dc-dim">{props.label}</span>
      <input type="search" data-testid={props.testId} value={props.value} placeholder={props.placeholder} autoComplete="off" onChange={(event) => props.onChange(event.target.value)} />
    </label>
  );
}

/**
 * The table of a paged tab, with its error, empty and pager states. An unreadable view is never shown as an empty
 * one: that is the difference between "nothing happened" and "you cannot see what happened".
 */
function PagedTable(props: {
  view: PolledView<object>;
  index: number;
  onPage: (index: number) => void;
  testId: string;
  empty: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const { view, index, testId } = props;
  const count = view.rows?.length ?? 0;
  const older = count > AUDIT_TAB_PAGE_ROWS;
  if (view.status === 'error') {
    return (
      <p className="dc-error" role="status" data-testid={`${testId}-error`}>
        {view.error}
      </p>
    );
  }
  return (
    <>
      {count === 0 && view.status === 'ready' ? (
        <p className="dc-dim" data-testid={`${testId}-empty`}>
          {index === 0 ? props.empty : 'No older records.'}
        </p>
      ) : (
        <div className="dc-table-scroll">
          <table className="dc-table" data-testid={`${testId}-table`}>
            {props.children}
          </table>
        </div>
      )}
      <div className="dc-pager">
        <button type="button" className="dc-button dc-button-quiet" data-testid={`${testId}-newer`} disabled={index === 0} onClick={() => props.onPage(index - 1)}>
          &larr; Newer
        </button>
        <span className="dc-dim" data-testid={`${testId}-page`}>
          Page {index + 1}
        </span>
        <button type="button" className="dc-button dc-button-quiet" data-testid={`${testId}-older`} disabled={!older} onClick={() => props.onPage(index + 1)}>
          Older &rarr;
        </button>
      </div>
    </>
  );
}

function When({ at }: { at: string }): React.JSX.Element {
  return (
    <time dateTime={at} title={at}>
      {new Date(at).toLocaleString()}
    </time>
  );
}

/**
 * A player, linked to their page. `name` is how the record names them; `now` — when it differs — is the name the
 * account carries today, so a renamed staff member is still recognisable.
 */
function PlayerLink({ uuid, name, now }: { uuid: string; name: string | null; now?: string | null }): React.JSX.Element {
  return (
    <>
      <Link to={`/player/${uuid}`}>{name ?? <em className="dc-null">never joined</em>}</Link>
      {now !== undefined && now !== null && now !== name && <span className="dc-dim"> (now {now})</span>}
    </>
  );
}

function Place(props: { dimension: string | null; x: number | null; y: number | null; z: number | null }): React.JSX.Element {
  if (props.dimension === null || props.x === null || props.y === null || props.z === null) return <span className="dc-null">—</span>;
  return (
    <span className="dc-num">
      {props.dimension} {Math.floor(props.x)} {Math.floor(props.y)} {Math.floor(props.z)}
    </span>
  );
}
